<script setup lang="ts">
const props = defineProps<{
  themeKeys: {
    bg: string;
    text: string;
    login: string;
    signUpBg: string;
    signUpFg: string;
  };
  links: {
    logo: string;
    pricing: string;
    features: string;
    resources: string;
    login: string;
    signup: string;
  };
}>();

const config = useState('CONTENT_CONFIG');
const lang = useLocalStorage('CONTENT_LANG', 'en');

const { data: dataQueried } = await useAsyncData('content-header-data', () =>
  queryCollection('header').all(),
);
const data = computed(() =>
  dataQueried.value?.find(
    (item) => item.title.toLowerCase() === lang.value?.toLowerCase(),
  ),
);

const menuOpen = ref(false);
const toggleMenu = () => {
  menuOpen.value = !menuOpen.value;
};

const route = useRoute();

const selectedTab = computed(() => {
  if (route.path.includes(props.links.pricing)) return 'pricing';
  if (route.path.includes(props.links.features)) return 'features';
  if (route.path.includes(props.links.resources)) return 'resources';
  return '';
});

// retractable
const { y: windowY, directions } = useWindowScroll();
const lastDirection = ref<'top' | 'bottom' | null>(null);
watch(directions, (newDirection) => {
  if (newDirection.top || newDirection.bottom) {
    lastDirection.value = newDirection.top ? 'top' : 'bottom';
  }
});

const shouldRetract = computed(() => {
  return lastDirection.value !== 'top' && windowY.value > 99;
});

// user is connected?
const { user } = useOidcAuth();
</script>

<template>
  <div
    class="sticky h-20 bg-transparent top-0 z-100"
    v-if="data"
  >
    <div
      class="px-8 xl:px-12 flex flex-row justify-between shadow gap-4 transition-all duration-300"
      :class="{ 'py-2 h-12': shouldRetract, 'py-4 h-20': !shouldRetract }"
      :style="{
        backgroundColor: props.themeKeys.bg,
        color: props.themeKeys.text,
      }"
    >
      <div
        class="shrink-0 transition-all duration-300"
        :class="{ 'h-8': shouldRetract, 'h-12': !shouldRetract }"
      >
        <NuxtLink :to="props.links.logo">
          <NuxtImg :src="config?.logo" role="decorative" class="h-full" />
        </NuxtLink>
      </div>
      <div
        class="hidden lg:flex flex-row items-center gap-8 justify-between *:cursor-pointer"
      >
        <NuxtLink
          :class="{ 'border-b-2 font-semibold': selectedTab === 'features' }"
          :style="{ borderColor: props.themeKeys.login }"
          :to="props.links.features"
        >
          {{ data.features }}
        </NuxtLink>
        <NuxtLink
          :class="{ 'border-b-2 font-semibold': selectedTab === 'pricing' }"
          :style="{ borderColor: props.themeKeys.login }"
          :to="props.links.pricing"
        >
          {{ data.pricing }}
        </NuxtLink>
        <!-- NuxtLink
          :class="{ 'border-b-2 font-semibold': selectedTab === 'resources' }"
          :style="{ borderColor: props.themeKeys.login }"
          :to="props.links.resources"
        >
          {{ data.resources }}
        </NuxtLink -->
        <template v-if="!user">
          <NuxtLink
            :to="props.links.login"
            :style="{ color: props.themeKeys.login }"
            class="font-semibold"
          >
            {{ data.login }}
          </NuxtLink>
          <NuxtLink
            :to="props.links.signup"
            class="px-4 rounded-lg hover:shadow-xl font-semibold transition-all duration-300"
            :class="{ 'py-1': shouldRetract, 'py-2': !shouldRetract }"
            :style="{
              backgroundColor: props.themeKeys.signUpBg,
              color: props.themeKeys.signUpFg,
            }"
          >
            {{ data.signup }}
          </NuxtLink>
        </template>
        <template v-else>
          <NuxtLink
            to="/app/projects"
            class="px-4 rounded-lg hover:shadow-xl font-semibold transition-all duration-300"
            :class="{ 'py-1': shouldRetract, 'py-2': !shouldRetract }"
            :style="{
              backgroundColor: props.themeKeys.signUpBg,
              color: props.themeKeys.signUpFg,
            }"
          >
            {{ data.app }}
          </NuxtLink>
        </template>
        <LangSwitcher
          :themeKeys="{ text: props.themeKeys.text }"
        />
      </div>
      <div class="flex lg:hidden flex-row items-center gap-4">
        <LangSwitcher
          :themeKeys="{ text: props.themeKeys.text }"
        />
        <button @click="toggleMenu()" class="h-full flex flex-row items-center">
          <Icon :name="menuOpen ? 'i-lucide-x' : 'i-lucide-menu'" class="size-6" />
        </button>
      </div>
      <div
        class="absolute top-6 sm:top-12 bottom-0 right-0 left-0 h-[100vh] w-full transition -z-10"
        :class="{ '-translate-y-[100%]': !menuOpen }"
        :style="{
          backgroundColor: props.themeKeys.bg,
          color: props.themeKeys.text,
        }"
      >
        <div class="flex flex-col gap-8 justify-center items-center mt-16 *:cursor-pointer">
          <NuxtLink :to="props.links.features">{{ data.features }}</NuxtLink>
          <NuxtLink :to="props.links.pricing">{{ data.pricing }}</NuxtLink>
          <!-- NuxtLink :to="props.links.resources">{{ data.resources }}</NuxtLink -->
          <template v-if="!user">
            <NuxtLink
              :to="props.links.login"
              :style="{ color: props.themeKeys.login }"
              class="font-semibold"
            >
              {{ data.login }}
            </NuxtLink>
            <NuxtLink
              :to="props.links.signup"
              class="px-6 py-2 rounded-xl font-semibold"
              :style="{
                backgroundColor: props.themeKeys.signUpBg,
                color: props.themeKeys.signUpFg,
              }"
            >
              {{ data.signup }}
            </NuxtLink>
          </template>
          <template v-else>
            <NuxtLink
              to="/app/projects"
              class="px-6 py-2 rounded-xl font-semibold"
              :style="{
                backgroundColor: props.themeKeys.signUpBg,
                color: props.themeKeys.signUpFg,
              }"
            >
              {{ data.app }}
            </NuxtLink>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
